import React, { useEffect, useState, useCallback } from 'react';
import { getProjects } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { FolderOpen, ArrowRight, TrendingUp, CheckCircle, Clock, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { projectStatus, projectStatusLabel, PROJECT_DONE_STATUSES } from '../../lib/workStatus';

const ProjectsList = () => {
  const { t } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchProjects = useCallback(async () => {
    try {
      const data = await getProjects();
      setProjects(data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const statusIcon = (status) => (PROJECT_DONE_STATUSES.includes(status) ? CheckCircle : status === 'in_progress' || status === 'active' ? TrendingUp : Clock);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-[#1F3D2B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="client-projects">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-[#2E2E2E] flex items-center gap-3" data-testid="client-projects-title">
          <FolderOpen className="w-8 h-8 text-[#1F3D2B]" />
          <span className="gradient-text">{t('portalUi.client.projects.title', 'Mani projekti')}</span>
        </h1>
        <p className="text-[#2E2E2E]/60 mt-1" data-testid="client-projects-subtitle">{t('portalUi.client.projects.subtitlePrefix', 'Jūsu')} <span className="text-[#1F3D2B] font-semibold">{t('portalUi.client.projects.subtitleHighlight', 'mežsaimniecības projekti')}</span> {t('portalUi.client.projects.subtitleSuffix', 'un to progress')}</p>
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {projects.map((project, index) => {
          const StatusIcon = statusIcon(project.status);
          const statusInfo = projectStatus(project.status);
          return (
            <Card
              key={project.id}
              className="hover-lift card-shine hover:shadow-2xl transition-all duration-300 cursor-pointer border-2 hover:border-[#1F3D2B]/30 overflow-hidden animate-fade-in"
              style={{ animationDelay: `${index * 0.1}s` }}
              onClick={() => navigate(`/portal/projects/${project.id}`)}
              data-testid={`client-project-card-${project.id}`}
            >
              {/* Decorative gradient overlay */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#1F3D2B]/5 to-transparent rounded-bl-full" />
              
              <CardHeader className="relative">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-xl mb-2 flex items-center gap-2">
                      {project.name}
                      <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                    </CardTitle>
                    {project.property_name && (
                      <p className="text-sm text-[#2E2E2E]/60 mb-1 flex items-center gap-1">
                        <span className="text-lg">📍</span> {project.property_name}
                      </p>
                    )}
                    {project.cadastral_number && (
                      <p className="text-xs text-[#2E2E2E]/50">
                      {t('portalUi.client.projects.cadastralLabel', 'Kadastra Nr.:')} {project.cadastral_number}
                      </p>
                    )}
                  </div>
                  <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${statusInfo.className}`} data-testid={`client-project-status-${project.id}`}>
                    <StatusIcon className="w-4 h-4" />
                    <span className="text-sm font-medium">{projectStatusLabel(t, project.status)}</span>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Progress */}
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-[#2E2E2E]/60">{t('portalUi.client.projects.progressLabel', 'Kopējais progress')}</span>
                    <span className="font-bold text-[#1F3D2B]">{project.progress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-gradient-to-r from-[#1F3D2B] via-[#2a5039] to-green-600 h-3 rounded-full transition-all duration-500 shadow-sm"
                      style={{ width: `${project.progress}%` }}
                    />
                  </div>
                </div>

                {/* Job Stats */}
                <div className="grid grid-cols-4 gap-3 pt-3 border-t">
                  <div className="text-center transform hover:scale-110 transition-transform">
                    <p className="text-xs text-[#2E2E2E]/60 mb-1">{t('portalUi.client.projects.jobsTotal', 'Kopā')}</p>
                    <div className="relative">
                      <p className="text-xl font-bold text-[#2E2E2E]">{project.total_jobs}</p>
                      <div className="absolute inset-0 bg-[#1F3D2B]/5 rounded-full blur-xl -z-10" />
                    </div>
                  </div>
                  <div className="text-center transform hover:scale-110 transition-transform">
                    <p className="text-xs text-green-600 mb-1">{t('portalUi.client.projects.jobsCompleted', 'Pabeigti')}</p>
                    <div className="relative">
                      <p className="text-xl font-bold text-green-600">{project.completed_jobs}</p>
                      <div className="absolute inset-0 bg-green-500/10 rounded-full blur-xl -z-10 animate-pulse" />
                    </div>
                  </div>
                  <div className="text-center transform hover:scale-110 transition-transform">
                    <p className="text-xs text-amber-600 mb-1">{t('portalUi.client.projects.jobsInProgress', 'Procesā')}</p>
                    <p className="text-xl font-bold text-amber-600">{project.in_progress_jobs}</p>
                  </div>
                  <div className="text-center transform hover:scale-110 transition-transform">
                    <p className="text-xs text-blue-600 mb-1">{t('portalUi.client.projects.jobsPlanned', 'Plānoti')}</p>
                    <p className="text-xl font-bold text-blue-600">{project.planned_jobs}</p>
                  </div>
                </div>

                {/* Kas notiks tālāk? */}
                {project.next_step && (
                  <div
                    className="bg-gradient-to-r from-blue-50 via-blue-100 to-indigo-50 border border-blue-200 rounded-lg p-3 animate-slide-in-left relative overflow-hidden"
                    data-testid={`client-project-next-step-${project.id}`}
                  >
                    <div className="absolute top-0 right-0 w-20 h-20 bg-blue-400/10 rounded-full blur-2xl" />
                    <p className="text-xs font-semibold text-blue-900 mb-1 flex items-center gap-2 relative">
                      <Calendar className="w-4 h-4 animate-float" />
                      {t('portalUi.client.projects.nextStepTitle', 'Kas notiks tālāk?')}
                    </p>
                    <p className="text-sm text-blue-800 relative">{project.next_step}</p>
                  </div>
                )}

                {/* View Details Button */}
                <Button
                  className="w-full bg-gradient-to-r from-[#1F3D2B] to-[#2a5039] hover:from-[#2a5039] hover:to-[#1F3D2B] text-white shadow-lg hover:shadow-xl transition-all duration-300 group"
                  onClick={() => navigate(`/portal/projects/${project.id}`)}
                  data-testid={`client-project-details-${project.id}`}
                >
                  {t('portalUi.client.projects.viewDetails', 'Skatīt detaļas')}
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {projects.length === 0 && (
        <div className="text-center py-16" data-testid="client-projects-empty">
          <FolderOpen className="w-20 h-20 mx-auto mb-4 text-[#2E2E2E]/20" />
          <h3 className="text-xl font-semibold text-[#2E2E2E] mb-2">{t('portalUi.client.projects.emptyTitle', 'Nav projektu')}</h3>
          <p className="text-[#2E2E2E]/60">{t('portalUi.client.projects.emptySubtitle', 'Jūsu projekti parādīsies šeit, kad administrators tos izveidos.')}</p>
        </div>
      )}
    </div>
  );
};

export default ProjectsList;